import React, { useCallback, useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import {
  AccidentImage,
  AccidentImageKeys,
  DestinationTypes,
  ImageInfo,
  EngineTroubleStatusTypes,
  QnStatesProps,
  TroubleSupportTypes,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { createOptions } from 'utils/commonUtils'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import RequestTemplate from 'components/templates/RequestTemplate'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import { success, fail } from 'utils/status'
import { initialQnEditState } from '../../../../../states/QnState'
import { getDomain, isKsDomain, isSaficDomain } from 'utils/domain'
import { transitionWithAction } from 'utils/commonUtils'
import InformationModal from 'components/elements/modal/InformationModal'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import DOMAIN from 'constants/domains'

const engineTroubleStatusOptions = createOptions(
  VEHICLE_STATE_FORM.engineTroubleStatus.options
)

const troubleSupportOptions = createOptions(
  VEHICLE_STATE_FORM.troubleSupport.options
)

/**
 * QN-03-b: バッテリー・エンジン関連
 */
const EngineTrouble: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const {
    engineTroubleStatus,
    troubleSupport,
    destinationType,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  /** React routerの遷移オブジェクト */
  const history = useHistory()

  /** 操作履歴のコンテキスト */
  const historyStackContext = useContext(HistoryStackContext)

  /** ページ遷移を行うコールバック */
  const transitionPage = useCallback((nextPagePath: string): void => {
    transitionWithAction(historyStackContext, 'OK', () => {
      history.push(nextPagePath)
    })
  }, [])

  /** バッテリー上がりモーダルの表示メッセージ */
  const MODAL_MESSAGE = 'バッテリージャンピングは共済期間中3回まで無料です。'

  /** バッテリー上がりモーダルの表示フラグ */
  const [isOpenModal, setIsOpenModal] = React.useState<boolean>(false)

  /** モーダルを閉じて次ページへ遷移を行う */
  const closeModal = React.useCallback(() => {
    setIsOpenModal(false)
    transitionPage(forwardTo)
  }, [])

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const isDisabled = React.useMemo(() => {
    if (
      //「バッテリーあがり」または「エンジンがかからない」かつ「現場での応急処置」選択時
      engineTroubleStatus.includes(EngineTroubleStatusTypes.battery) ||
      (engineTroubleStatus.includes(EngineTroubleStatusTypes.engineNotStart) &&
        troubleSupport.includes(TroubleSupportTypes.emergencyMeasure))
    ) {
      return false
    }
    if (
      // 運び先指定を選択時に運び先を入力していない時
      destinationType.includes(DestinationTypes.specify) &&
      !editState.destinationLocation.isSelected
    ) {
      return true
    }
    if (
      // 運び先指定を選択時に運び先を入力している時
      destinationType.includes(DestinationTypes.specify) &&
      editState.destinationLocation.isSelected
    ) {
      return addressMatchError
    }
    return false
  }, [
    editState.vehicleStateInfo,
    editState.destinationLocation,
    addressMatchError,
  ])

  // 注意メッセージ
  const warning = React.useMemo(() => {
    if (
      // 「バッテリー上がり」選択時
      engineTroubleStatus.includes(EngineTroubleStatusTypes.battery)
    ) {
      return WARNING.CAN_WRECKER
    }
    if (
      // 「エンジンがかからない」かつ「現場での応急処置」選択時
      engineTroubleStatus.includes(EngineTroubleStatusTypes.engineNotStart) &&
      troubleSupport.includes(TroubleSupportTypes.emergencyMeasure)
    ) {
      return ''
    }
    switch (destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
  }, [editState.vehicleStateInfo])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  const handleTroubleStatusChanged = React.useCallback(
    (value: EngineTroubleStatusTypes) => {
      setVehicleStateInfo({
        engineTroubleStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleTroubleSupportChanged = React.useCallback(
    (value: TroubleSupportTypes) => {
      setVehicleStateInfo({
        troubleSupport: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo) => {
      const accidentImage: AccidentImage = {
        ...editState.vehicleStateInfo.accidentImage,
      }
      accidentImage[name] = imageInfo
      setVehicleStateInfo({
        accidentImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    const QnStateDefaultInfoImage =
      initialQnEditState.vehicleStateInfo.accidentImage
    setEditingItem({
      ...editState,
      vehicleStateInfo: {
        ...editState.vehicleStateInfo,
        accidentImage: editState.vehicleStateInfo.isAddPhoto
          ? {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
            }
          : {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
              vehicleCloseUp: QnStateDefaultInfoImage.vehicleCloseUp,
              landmark: QnStateDefaultInfoImage.landmark,
              carVerification: QnStateDefaultInfoImage.carVerification,
              supplement: QnStateDefaultInfoImage.supplement,
            },
      },
    })
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: {
        ...editState.vehicleStateInfo,
        accidentImage: editState.vehicleStateInfo.isAddPhoto
          ? {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
            }
          : {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
              vehicleCloseUp: QnStateDefaultInfoImage.vehicleCloseUp,
              landmark: QnStateDefaultInfoImage.landmark,
              carVerification: QnStateDefaultInfoImage.carVerification,
              supplement: QnStateDefaultInfoImage.supplement,
            },
      },
      destinationLocation: editState.destinationLocation,
    })
    // バッテリー上がり選択時 かつ 共済系ドメイン時は決定ボタン押下時に遷移せずモーダルを表示する
    if (
      engineTroubleStatus.includes(EngineTroubleStatusTypes.battery) &&
      isKsDomain()
    ) {
      setIsOpenModal(true)
      return fail()
    }
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])
  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      engineTroubleStatus:
        engineTroubleStatus === ''
          ? EngineTroubleStatusTypes.battery
          : engineTroubleStatus,
      troubleSupport:
        troubleSupport === ''
          ? TroubleSupportTypes.emergencyMeasure
          : troubleSupport,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  return (
    <RequestTemplate
      title={'バッテリー・エンジン関連'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedImageGroup
            accidentImage={editState.vehicleStateInfo.accidentImage}
            title={`${VEHICLE_STATE_FORM.accidentImage.description}`}
            isSinglePhoto={true}
            isRequired={!editState.vehicleStateInfo.noPhoto}
            onChanged={handleImageChanged}
            addPhoto={editState.vehicleStateInfo.isAddPhoto}
          />
          <PhotoPointNote />
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.engineTroubleStatus.formName}を選択`}
            isRequired={true}
            options={engineTroubleStatusOptions}
            selectedValue={engineTroubleStatus}
            onValueChanged={handleTroubleStatusChanged}
          />
          {engineTroubleStatus.includes(
            EngineTroubleStatusTypes.engineNotStart
          ) && (
            <RadioButtonGroup
              title={`${VEHICLE_STATE_FORM.troubleSupport.formName}`}
              isRequired={true}
              options={troubleSupportOptions}
              selectedValue={troubleSupport}
              onValueChanged={handleTroubleSupportChanged}
            />
          )}
          {!(
            engineTroubleStatus.includes(EngineTroubleStatusTypes.battery) ||
            (engineTroubleStatus.includes(
              EngineTroubleStatusTypes.engineNotStart
            ) &&
              troubleSupport.includes(TroubleSupportTypes.emergencyMeasure))
          ) && (
            //「バッテリーあがり」または「エンジンがかからない」かつ「現場での応急処置」選択時以外に車高改造の文言を表示する
            <>
              {
                // 共済系ドメイン時の場合、車高改造の文言を変更する
                isKsDomain() ? (
                  <KsSelectVehicleModificationContent />
                ) : (
                  <SelectVehicleModificationContent />
                )
              }
            </>
          )}
          {
            // 「バッテリー上がり」かつ「共済系ドメイン時」は決定ボタン押下時にモーダルを表示
            <InformationModal
              on={isOpenModal}
              content={MODAL_MESSAGE}
              greyBack
              textCenter={false}
              closeLabel="OK"
              closeFunction={closeModal}
            />
          }
          {
            // 「レッカー搬送」または「走行中のエンスト」または「オーバーヒート」選択時
            (engineTroubleStatus.includes(
              EngineTroubleStatusTypes.engineStop
            ) ||
              engineTroubleStatus.includes(EngineTroubleStatusTypes.overheat) ||
              (engineTroubleStatus.includes(
                EngineTroubleStatusTypes.engineNotStart
              ) &&
                troubleSupport.includes(TroubleSupportTypes.wrecker))) && (
              <>
                {isKsDomain() ? (
                  <KsSelectDestinationContent isIntroduction={true} />
                ) : (
                  <SelectDestinationContent />
                )}
              </>
            )
          }
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      notClearEditingItem
      isDisabled={isDisabled}
    />
  )
}

export default EngineTrouble
